import { Directive, ElementRef, OnInit, OnDestroy, NgZone } from '@angular/core';

/**
 * Directive to fix Kendo DatePicker scroll behavior
 * Prevents the calendar from jumping to top when scrolling
 */
@Directive({
  selector: '[preventScrollPropagation]',
  standalone: true
})
export class PreventScrollPropagationDirective implements OnInit, OnDestroy {
  private static globalSetupDone = false;
  private static globalClickHandler: ((e: Event) => void) | null = null;
  private mutationObserver: MutationObserver | null = null;
  private processedContainers = new Set<Element>();
  private checkInterval: any;
  private isDestroyed = false;

  constructor(
    private el: ElementRef,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      // Setup global handler once for all instances
      if (!PreventScrollPropagationDirective.globalSetupDone) {
        this.setupGlobalHandler();
        PreventScrollPropagationDirective.globalSetupDone = true;
      }

      // Instance-specific setup
      this.setupScrollFix();
      setTimeout(() => !this.isDestroyed && this.setupScrollFix(), 100);
      setTimeout(() => !this.isDestroyed && this.setupScrollFix(), 300);

      // Limited periodic check
      let checkCount = 0;
      this.checkInterval = setInterval(() => {
        if (!this.isDestroyed && checkCount < 10) {
          this.setupScrollFix();
          checkCount++;
        } else if (this.checkInterval) {
          clearInterval(this.checkInterval);
        }
      }, 500);

      this.startObserver();
    });
  }

  private setupGlobalHandler() {
    // Global click handler for ALL Kendo calendar navigation
    PreventScrollPropagationDirective.globalClickHandler = (e: Event) => {
      const target = e.target as HTMLElement;

      // Check if click is on calendar navigation
      const isNavButton = target.closest('.k-calendar-nav button, .k-nav-prev, .k-nav-next, .k-calendar-title, .k-button');

      if (isNavButton) {
        // Find the calendar container
        const popup = target.closest('.k-animation-container, .k-popup');
        if (!popup) return;

        const calendar = popup.querySelector('.k-calendar, .k-calendar-view') as HTMLElement;
        if (!calendar) return;

        const savedScrollTop = calendar.scrollTop || 0;
        const savedScrollLeft = calendar.scrollLeft || 0;

        // NEW APPROACH: Listen to scroll events and immediately restore
        let isRestoring = false;
        const scrollListener = (ev: Event) => {
          if (isRestoring) return;

          const currentScroll = calendar.scrollTop;
          if (currentScroll !== savedScrollTop) {
            isRestoring = true;
            calendar.scrollTop = savedScrollTop;
            calendar.scrollLeft = savedScrollLeft;
            isRestoring = false;
          }
        };

        // Add scroll listener immediately
        calendar.addEventListener('scroll', scrollListener, { passive: false });

        // Also restore on animation frame
        requestAnimationFrame(() => {
          calendar.scrollTop = savedScrollTop;
          calendar.scrollLeft = savedScrollLeft;

          // And after small delays
          setTimeout(() => {
            calendar.scrollTop = savedScrollTop;
            calendar.scrollLeft = savedScrollLeft;
          }, 10);

          setTimeout(() => {
            calendar.scrollTop = savedScrollTop;
            calendar.scrollLeft = savedScrollLeft;
          }, 50);

          setTimeout(() => {
            calendar.scrollTop = savedScrollTop;
            calendar.scrollLeft = savedScrollLeft;
          }, 100);
        });

        // Remove listener after 300ms
        setTimeout(() => {
          calendar.removeEventListener('scroll', scrollListener);
        }, 300);
      }
    };

    // Add to document with capture
    document.addEventListener('click', PreventScrollPropagationDirective.globalClickHandler, { capture: true, passive: true });
  }

  private startObserver() {
    if (this.mutationObserver || this.isDestroyed) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      if (this.isDestroyed) return;

      const hasNewPopups = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node =>
          node instanceof Element &&
          (node.classList.contains('k-animation-container') ||
           node.classList.contains('k-popup'))
        )
      );

      if (hasNewPopups) {
        setTimeout(() => !this.isDestroyed && this.setupScrollFix(), 50);
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: false
    });
  }

  private setupScrollFix() {
    if (this.isDestroyed) return;

    const popupContainers = document.querySelectorAll('.k-animation-container, .k-popup');

    popupContainers.forEach(container => {
      if (this.processedContainers.has(container)) {
        return;
      }

      this.processedContainers.add(container);

      // Stop wheel propagation
      container.addEventListener('wheel', (e: Event) => {
        e.stopPropagation();
      }, { passive: true, capture: true });

      // Stop touch propagation
      container.addEventListener('touchmove', (e: Event) => {
        e.stopPropagation();
      }, { passive: true, capture: true });
    });
  }

  ngOnDestroy() {
    this.isDestroyed = true;

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.processedContainers.clear();

    // Note: We don't remove the global handler as other instances might still need it
  }
}
